import { useEffect, useRef, useState } from 'react';
import { formatPhoneNumber, type CallNoteDestination, type Transcript } from '@avslack/shared';
import { getCallNote, getTranscript, sendCallNote } from '../lib/api';
import { useUsers } from '../data/users';
import { Icon } from './Icon';

/**
 * HIVASJEGYZET - a kolléga ugyfelrendszere szerint a TT-be, a ProjectFlow-
 * feladatra, vagy csak az AVSlackbe (a szerver donti el, lasd tt-note.ts). A
 * felulet megmondja, hova megy, es a mentett jegyzetet visszatolti.
 *
 * ─── Miert nem megy semmi automatikusan? ────────────────────────────────────
 *
 * Mert ez az ugyfel ADATLAPJARA kerul, a kolléga neveben, es evekig ott marad.
 * A gepi atirat hibazik - neveket, gyogyszerneveket, szamokat -, es egy
 * felreirt mondat az adatlapon rosszabb, mint a hianyzo jegyzet. Ezert a
 * kolléga szerkeszti, es O kuldi el (Adam dontese 2026-09-22).
 *
 * ─── Hivas KOZBEN es utana ──────────────────────────────────────────────────
 *
 * Kozben: a kolléga gépel, ahogy elhangzik. Atirat ilyenkor NEM toltodik be -
 * meg nem teljes, es egy folyamatosan valtozo mezobe gepelni lehetetlen.
 *
 * Utana: ha a jegyzet meg ures, az atirat automatikusan bekerul; ha mar van
 * benne szoveg, kulon gombbal fuzheto hozza. Amit a kolléga irt, azt SOHA nem
 * irjuk felul - ez a `touched` jelzes dolga.
 *
 * A piszkozat a bongeszoben marad (huddle-onkent), igy egy veletlen bezaras
 * vagy ablakvaltas nem viszi el a hivas kozben gépelt jegyzetet.
 *
 * ─── Javithato ──────────────────────────────────────────────────────────────
 *
 * Ugyanarra a hivasra masodszor kuldve a TT FELULIRJA a korabbit (a huddle
 * azonositojan egyedi index van). Igy egy elgepelés utolag rendbe tehato - ezt
 * a felulet ki is mondja, hogy a kolléga merjen ujra kuldeni.
 */
export function TtCallNote({
  huddleId,
  number,
  live = false,
  embedded = false,
  onClose,
}: {
  huddleId: string;
  number: string | null;
  /** Tart-e meg a hivas: ettol fugg, hogy az atirat teljes-e. */
  live?: boolean;
  /** Oldalpanelben all - nincs sajat kerete es "Mégse" gombja. */
  embedded?: boolean;
  onClose?: () => void;
}) {
  const { nameOf } = useUsers();
  const [text, setText] = useState(() => readDraft(huddleId));
  const [tema, setTema] = useState(() => readDraft(huddleId, 'tema'));
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'sent' | 'updated' | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Hova megy a mentes - amig nem tudjuk, semleges feliratok. */
  const [destination, setDestination] = useState<CallNoteDestination | null>(null);
  /** Irt-e mar bele a kolléga. Ha igen, az atirat nem irhatja felul. */
  const touched = useRef(readDraft(huddleId).length > 0);

  /*
    A MENTETT jegyzet visszajon - ha nincs helyi piszkozat. Ami a kolléga
    bongeszojeben felig kesz all, az frissebb, mint a korabban elmentett.
  */
  useEffect(() => {
    let cancelled = false;
    void getCallNote(huddleId)
      .then((state) => {
        if (cancelled) return;
        setDestination(state.destination);
        if (state.note && !touched.current) {
          touched.current = true;
          setText(state.note.text);
          setTema(state.note.topic ?? '');
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [huddleId]);
  const copy = noteCopy(destination);

  // Piszkozat: egy veletlen bezaras ne vigye el a hivas kozben irtakat.
  useEffect(() => {
    writeDraft(huddleId, text, tema);
  }, [huddleId, text, tema]);

  /*
    Az atirat CSAK a hivas utan jon be - kozben meg nem teljes, es egy
    folyamatosan valtozo mezobe nem lehet gepelni.
  */
  useEffect(() => {
    if (live) return;
    let cancelled = false;
    void getTranscript(huddleId)
      .catch(() => null)
      .then((loaded) => {
        if (cancelled || !loaded) return;
        setTranscript(loaded);
        if (!touched.current) setText((current) => current || asText(loaded, nameOf));
      });
    return () => {
      cancelled = true;
    };
  }, [huddleId, live]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (value: string) => {
    touched.current = true;
    setText(value);
  };

  const appendTranscript = () => {
    if (!transcript) return;
    touched.current = true;
    setText((current) => {
      const body = asText(transcript, nameOf);
      return current.trim() ? `${current.trimEnd()}\n\n${body}` : body;
    });
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await sendCallNote(huddleId, text, tema || undefined);
      setDestination(result.destination);
      if (!result.ok) {
        setError(noteCopy(result.destination).failed);
        return;
      }
      setDone(result.updated ? 'updated' : 'sent');
      clearDraft(huddleId);
    } catch {
      setError(copy.failed);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Frame embedded={embedded}>
        <p className="text-xs text-ink">
          <Icon name="pipa" className="mr-1 inline size-3 text-accent" />
          {done === 'updated' ? copy.updated : copy.sent}
        </p>
        <div className="mt-2 flex items-center gap-3">
          {/*
            "Még írok hozzá", nem "Bezárom": a TT felulirja a korabbit, tehat a
            kolléga nyugodtan visszaterhet - ezt a gomb szovege mondja ki.
          */}
          <button
            type="button"
            onClick={() => setDone(null)}
            className="text-xs text-ink-muted hover:text-ink"
          >
            Még írok hozzá
          </button>
          {!embedded && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Bezárom
            </button>
          )}
        </div>
      </Frame>
    );
  }

  return (
    <Frame embedded={embedded}>
      <p className="mb-2 text-xs text-ink-muted">
        {live
          ? `${copy.title} – írhatsz közben is; az átirat a hívás után fűzhető hozzá.`
          : `${copy.title}${number ? ` – ${formatPhoneNumber(number)}` : ''}. Az átiratot átírhatod; ami itt áll, az kerül mentésre.`}
      </p>
      <input
        aria-label="Téma"
        value={tema}
        onChange={(event) => setTema(event.target.value)}
        placeholder="Téma (pl. Cloud belépés nem megy)…"
        className="mb-2 w-full rounded-control border border-border bg-surface px-2 py-1 text-xs"
      />
      <textarea
        aria-label="Jegyzet"
        value={text}
        onChange={(event) => edit(event.target.value)}
        rows={embedded ? 12 : 10}
        placeholder="Amit megbeszéltetek…"
        className="w-full resize-y rounded-control border border-border bg-surface px-2 py-1.5 font-mono text-xs leading-relaxed"
      />
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !text.trim()}
          className="rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink disabled:opacity-50"
        >
          {busy ? 'Mentés…' : copy.button}
        </button>
        {/* Csak akkor kinaljuk, ha VAN mit hozzafuzni, es a mezo nem ures. */}
        {transcript && transcript.segments.length > 0 && text.trim() && (
          <button
            type="button"
            onClick={appendTranscript}
            className="rounded-control border border-border px-2 py-1 text-xs text-ink-muted hover:text-ink"
          >
            Átirat hozzáfűzése
          </button>
        )}
        {!embedded && onClose && (
          <button type="button" onClick={onClose} className="text-xs text-ink-muted hover:text-ink">
            Mégse
          </button>
        )}
      </div>
      <p className="mt-1.5 text-2xs text-ink-faint">
        Újra elmentve felülírja a korábbit – javítani lehet.
      </p>
    </Frame>
  );
}

/** A feliratok aszerint, hova megy a jegyzet. */
function noteCopy(destination: CallNoteDestination | null) {
  switch (destination) {
    case 'tt':
      return {
        title: 'Jegyzet a TT-be',
        button: 'Küldés a TT-be',
        sent: 'A jegyzet felkerült a TT ügyfélkártyájára.',
        updated: 'A jegyzet frissült a TT-ben.',
        failed: 'A TT nem vette át a jegyzetet. Próbáld újra – a szöveg itt marad.',
      };
    case 'projectflow':
      return {
        title: 'Jegyzet a ProjectFlow-feladatra',
        button: 'Mentés a feladatra',
        sent: 'A jegyzet felkerült a ProjectFlow-feladatra.',
        updated: 'A jegyzet frissült a ProjectFlow-feladaton.',
        failed: 'Nem sikerült menteni a jegyzetet. Próbáld újra – a szöveg itt marad.',
      };
    default:
      return {
        title: 'Hívásjegyzet (az AVSlackben marad)',
        button: 'Mentés',
        sent: 'A jegyzet elmentve.',
        updated: 'A jegyzet frissítve.',
        failed: 'Nem sikerült menteni a jegyzetet. Próbáld újra – a szöveg itt marad.',
      };
  }
}

const Frame = ({ embedded, children }: { embedded: boolean; children: React.ReactNode }) =>
  embedded ? (
    <div>{children}</div>
  ) : (
    <div className="w-full rounded-card border border-border-soft bg-surface-raised p-3">
      {children}
    </div>
  );

// ─── Piszkozat a bongeszoben ─────────────────────────────────────────────────

/*
  Bongeszo-tarolo, nem szerver: ez egy FELIG kesz szoveg, ami csak az iro
  kollegara tartozik, amig el nem kuldi. Ha a tarolo nem elerheto (privat
  ablak, letiltott sutik), a mezo ettol meg mukodik - csak nem emlekszik.
*/
const draftKey = (huddleId: string, field = 'text') => `avslack:tt-note:${huddleId}:${field}`;

function readDraft(huddleId: string, field = 'text'): string {
  try {
    return localStorage.getItem(draftKey(huddleId, field)) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(huddleId: string, text: string, tema: string): void {
  try {
    localStorage.setItem(draftKey(huddleId), text);
    localStorage.setItem(draftKey(huddleId, 'tema'), tema);
  } catch {
    // Tarolo nelkul legfeljebb nem emlekszik - a jegyzet igy is elkuldheto.
  }
}

function clearDraft(huddleId: string): void {
  try {
    localStorage.removeItem(draftKey(huddleId));
    localStorage.removeItem(draftKey(huddleId, 'tema'));
  } catch {
    // Lasd fent.
  }
}

/**
 * Az atirat szoveggé - beszelonkent, idobelyeg nelkul.
 *
 * Idobelyeg NINCS benne: a TT-ben ezt emberek olvassak egy adatlapon, ahol a
 * "mikor hangzott el a hivason belul" nem segit, csak zajt visz. A BESZELO
 * viszont kell, kulonben nem lehet kibogozni, mit mondott az ugyfel, es mit a
 * kolléga.
 */
function asText(transcript: Transcript, nameOf: (id: string) => string): string {
  return transcript.segments
    .map((segment) => {
      const speaker = segment.userId
        ? nameOf(segment.userId)
        : (segment.speakerLabel ?? 'Hívó fél');
      return `${speaker}: ${segment.polishedText ?? segment.text}`;
    })
    .join('\n');
}
